import React from "react";
import { useTranslation } from "react-i18next";

import { CustomBicycleFilterKey } from "../../../../commons/specs/bicycle";
import { ActiveFilters, FilterConfigItem, FilterKey, ProductFilterValues } from "../../../../commons/specs/filters";
import { ProductType } from "../../../../commons/specs/product";
import { Brand } from "../../../../commons/types/brand";
import FilterListItem from "../../components/FilterListItem/FilterListItem";
import Tooltip, { TooltipPosition } from "../../components/Tooltip/Tooltip";
import config from "../../config";
import { FilterItemDisabledType, getFilterItemDisabledSettings, isFilterItemActive } from "../../libs/filters";
import useIntersect from "../../libs/hooks/use-intersect";
import usePrevious from "../../libs/hooks/use-previous";

type FilterListItemArgs = React.ComponentProps<typeof FilterListItem>;

type Props<Type extends ProductType> = FilterListItemArgs & {
  filterKey: FilterKey<Type>;
  filterConfig: FilterConfigItem<Type, FilterKey<Type>>;
  filterValues: ProductFilterValues<Type>;
  selectedBrands: Brand[];
  activeFilters: ActiveFilters<Type>;
  filterKeysWithInsufficientFilterValues: FilterKey<Type>[];
  variantsLength: number;
  isOffline: boolean;
  preventTooltip: boolean;
  warningReason?: string;
  disabledReason?: string;
  scrollRef?: React.RefObject<HTMLDivElement | null>;
};

const InnerTooltip = ({
  preventTooltip,
  open,
  scrollRef,
  ...args
}: FilterListItemArgs & {
  preventTooltip: boolean;
  open: () => void;
  scrollRef?: React.RefObject<HTMLDivElement | null>;
}) => {
  const [setNode, observerEntry] = useIntersect({ rootRef: scrollRef });
  const prevPreventTooltip = usePrevious(preventTooltip);

  React.useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (!preventTooltip && observerEntry?.isIntersecting && args.warning) {
      // Await transition duration before showing Tooltip if tooltip was previously prevented
      timeout = setTimeout(open, prevPreventTooltip ? 300 : 0);
    }

    return () => clearTimeout(timeout);
  }, [preventTooltip, args.warning, observerEntry?.isIntersecting]); // eslint-disable-line react-hooks/exhaustive-deps

  return <FilterListItem ref={setNode} {...args} />;
};

const FilterListItemTooltipPartial = <Type extends ProductType>({
  filterKey,
  filterConfig,
  filterValues,
  selectedBrands,
  activeFilters,
  filterKeysWithInsufficientFilterValues,
  variantsLength,
  isOffline,
  preventTooltip,
  warningReason: outerWarningReason,
  disabledReason: outerDisabledReason,
  ...args
}: Props<Type>) => {
  const { t } = useTranslation(["commons"]);

  const innerDisabledReason = React.useMemo(() => {
    const { filterItemDisabledType, disabledConfigs } = getFilterItemDisabledSettings(
      filterKey,
      filterConfig,
      filterValues,
      selectedBrands,
      activeFilters,
      filterKeysWithInsufficientFilterValues,
      isOffline
    );

    if (filterItemDisabledType === FilterItemDisabledType.InsufficientDataQuality) {
      const brands = disabledConfigs
        .filter(({ years }) => years.length)
        .map(({ brand, years }) => `${brand} ${years.join(", ")}`);

      return t("commons:filterListItemTooltipPartialProductFinderOverview.productDataQualityInsufficientTooltip", {
        brands: brands.join(", ")
      });
    } else if (filterItemDisabledType === FilterItemDisabledType.InsufficientFilterOptions) {
      const brands = disabledConfigs.map(({ brand }) => brand);

      return t("commons:filterListItemTooltipPartialProductFinderOverview.filterOptionNotApplicableTooltip", {
        brands: brands.join(", ")
      });
    } else if (filterItemDisabledType === FilterItemDisabledType.Offline) {
      return t("commons:filterListItemTooltipPartialProductFinderOverview.filterOfflineUnavailableTooltip");
    }
  }, [
    t,
    filterKey,
    filterConfig,
    filterValues,
    selectedBrands,
    activeFilters,
    filterKeysWithInsufficientFilterValues,
    isOffline
  ]);

  const innerWarningReason = React.useMemo(() => {
    if (
      filterKey === CustomBicycleFilterKey.Sizing &&
      isFilterItemActive(filterKey, filterConfig, activeFilters) &&
      variantsLength > config.shared.filter.sizingLimit
    ) {
      return t("commons:filterListItemTooltipPartialProductFinderOverview.sizingWarningTooltip");
    }
  }, [t, filterKey, filterConfig, activeFilters, variantsLength]);

  const disabledReason = outerDisabledReason ?? innerDisabledReason;
  const warningReason = outerWarningReason ?? innerWarningReason;
  const tooltipReason = disabledReason ?? warningReason;
  const isTooltipReasonWarning = !disabledReason && !!warningReason;

  return tooltipReason ? (
    <Tooltip
      key={tooltipReason}
      maxWidth="s"
      preferredPosition={TooltipPosition.Left}
      content={tooltipReason}
      isCloseable={isTooltipReasonWarning}
    >
      {({ open }) => (
        <InnerTooltip
          onDisabledClick={open}
          disabled={!!disabledReason}
          warning={isTooltipReasonWarning}
          preventTooltip={preventTooltip}
          open={open}
          {...args}
        />
      )}
    </Tooltip>
  ) : (
    <FilterListItem {...args} />
  );
};

export default FilterListItemTooltipPartial;
